import React, { memo, useMemo } from 'react'
import { Typography } from '@mui/material'
import tw from 'twin.macro'
import { colors } from 'src/theme'
import { FileWithPreview } from 'src/components/shared/FileWithPreview'
import { CollectedData, CollectedDataDocument } from 'src/interfaces/collectedData'
import { CollectedDataStepType } from 'src/constants/collectedData'
import { addS } from 'src/utils/baseUtils'
import { getFileNameWithoutExtension } from 'src/utils/fileDownload'

interface Props {
  collectedData: CollectedData
  header?: any
  partnerDriverId: string
}

const PDF_FILE_TYPE = 'pdf'
export const FULL_PACKAGE_FILE_NAME = 'Full Package.pdf'

const getUniqueDocumentsByTitle = (documents: CollectedDataDocument[]): CollectedDataDocument[] => {
  const seen = new Set<string>()

  return documents.filter((d) => {
    const title = getFileNameWithoutExtension(d.fileName, d.extension).toLowerCase()

    if (!title) {
      return true
    }

    if (seen.has(title)) {
      return false
    }

    seen.add(title)

    return true
  })
}

const sortSignContractDocuments = (documents: CollectedDataDocument[]): CollectedDataDocument[] => {
  const fullPackage = documents.filter((d) => d.fileName === FULL_PACKAGE_FILE_NAME)
  const rest = documents.filter((d) => d.fileName !== FULL_PACKAGE_FILE_NAME)

  return getUniqueDocumentsByTitle([...fullPackage, ...rest])
}

const ContractorStepDocumentsComponent = ({ header, collectedData, partnerDriverId }: Props) => {
  const documents = collectedData.documents?.length
    ? collectedData.documents
    : collectedData.lastRejectionDocuments || []

  const isSignContract = collectedData.collectDataStep.type === CollectedDataStepType.SignContract

  const sortedDocuments = useMemo(
    () => (isSignContract ? sortSignContractDocuments(documents) : documents),
    [documents, isSignContract],
  )

  if (!documents.length) {
    return null
  }

  const renderFilePreview = (document: CollectedDataDocument) => (
    <FileWithPreview
      key={document.filePath}
      fullScreen={document.extension === PDF_FILE_TYPE}
      filePath={document.filePath}
      fileName={document.fileName}
      partnerDriverId={partnerDriverId}
      stepId={document.collectDataStepId ?? collectedData.collectDataStep.id}
      documentId={document.id}
      fileExtension={document.extension}
      header={header}
      previewContent={
        document.extension === PDF_FILE_TYPE ? (
          <div css={tw`w-full max-h-[50vh] overflow-hidden`}>
            <object
              data={`${document.filePath}#toolbar=0&scrollbar=0&view=FitH`}
              type="application/pdf"
              css={tw`w-[calc(100%+20px)] h-[calc(100%+20px)] pointer-events-none`}
            />
          </div>
        ) : (
          <img
            src={document.filePath}
            alt={document.fileName}
            decoding="async"
            loading="lazy"
            css={tw`block w-full h-auto min-h-[100px] max-h-[50vh] object-contain`}
          />
        )
      }
      className={tw`rounded-lg mb-3`}
    />
  )

  if (isSignContract) {
    return (
      <div css={tw`mt-3`}>
        {sortedDocuments.map((document, index) => (
          <div key={document.filePath}>
            {index > 0 && <div css={tw`w-full h-px mt-[2px] mb-4 bg-[#EDEDED]`} />}
            <Typography css={tw`mt-3`} color={colors.SHADES_GREY_400} variant="h6">
              Attached file
            </Typography>
            <Typography
              css={tw`mb-2 break-all font-semibold`}
              color={colors.BASICS_BLACK}
              variant="body1"
            >
              {getFileNameWithoutExtension(document.fileName, document.extension)}
            </Typography>
            {renderFilePreview(document)}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div css={tw`mt-3`}>
      <Typography css={tw`mb-1.5`} color={colors.SHADES_GREY_400} variant="h6">
        {addS(documents.length, 'Attached file')}
      </Typography>
      {documents.map(renderFilePreview)}
    </div>
  )
}

export const ContractorStepDocuments = memo(ContractorStepDocumentsComponent)
